// ABOUTME: International postcode detection, validation and coarsening utilities
// ABOUTME: Handles country detection from postcode patterns and privacy-preserving coarsening

export type CountryCode = 'GB' | 'US' | 'CA' | 'IE' | 'NL' | 'MT' | 'AU';

/**
 * Regex patterns for different postcode formats
 * These patterns are collision-free for the launch countries
 */
const PATTERNS = {
  // UK full postcode: SW1A 1AA, HP22 7BH
  GB_FULL: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[ABD-HJLNP-UW-Z]{2}$/,
  // UK outward code only: HP22, SW1A (legacy format already in system)
  GB_OUTWARD: /^[A-Z]{1,2}\d[A-Z\d]?$/,
  // US ZIP code: 90210 (exactly 5 digits)
  US: /^\d{5}$/,
  // Canada: M4C 1B5 (L D L ␠ D L D format)
  CA: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/,
  // Ireland: D02 XY45 (L D D ␠ XXXX format)
  IE: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/,
  // Netherlands: 1013 AB (D D D D ␠ LL format)
  NL: /^\d{4}\s?[A-Z]{2}$/,
  // Malta: VLT 1234 (LLL ␠ DDDD format)
  MT: /^[A-Z]{3}\s?\d{4}$/,
  // Australia: 2000 (exactly 4 digits)
  AU: /^\d{4}$/,
} as const;

/**
 * Privacy-preserving coarsening rules
 * Reduces postcodes to area-level precision only
 */
const COARSENING_RULES = {
  GB: (postcode: string): string => {
    const normalized = postcode.trim().toUpperCase();

    // If it's already an outward code (legacy format), return as-is
    if (PATTERNS.GB_OUTWARD.test(normalized)) {
      return normalized;
    }

    // For full postcodes, use a more direct approach
    // UK full postcode pattern: outward code + space + inward code
    if (normalized.includes(' ')) {
      // Split on space and take first part
      return normalized.split(' ')[0];
    } else {
      // No space - try to extract outward code from concatenated postcode
      // Use the full UK postcode regex to validate and extract
      const fullMatch = normalized.match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[ABD-HJLNP-UW-Z]{2})$/);
      if (fullMatch) {
        return fullMatch[1]; // Return the outward code part
      }
    }

    // Fallback - should not happen with valid postcodes
    return normalized;
  },

  US: (postcode: string): string => {
    // US ZIP codes are already area-level (5 digits)
    return postcode.trim();
  },

  CA: (postcode: string): string => {
    // Canadian FSA (first 3 characters)
    return postcode.replace(/\s+/g, '').slice(0, 3).toUpperCase();
  },

  IE: (postcode: string): string => {
    // Irish routing key (first 3 characters)
    return postcode.replace(/\s+/g, '').slice(0, 3).toUpperCase();
  },

  NL: (postcode: string): string => {
    // Netherlands first 4 digits
    return postcode.replace(/\s+/g, '').slice(0, 4);
  },

  MT: (postcode: string): string => {
    // Malta first 3 letters (LLL ␠ DDDD format)
    const normalized = postcode.trim().toUpperCase();
    if (normalized.includes(' ')) {
      return normalized.split(' ')[0];
    } else {
      // No space - extract first 3 letters from LLL1234 format
      const match = normalized.match(/^([A-Z]{3})(\d{4})$/);
      if (match) {
        return match[1];
      }
    }
    return normalized;
  },

  AU: (postcode: string): string => {
    // Australian postcodes are already area-level (4 digits)
    return postcode.trim();
  },
} as const;

/**
 * Detects the country code from a postcode string
 * Returns null if no pattern matches
 */
export function detectCountry(postcode: string): CountryCode | null {
  const normalized = postcode.trim().toUpperCase();

  // Check full patterns first - order matters to avoid collisions
  if (PATTERNS.GB_FULL.test(normalized)) return 'GB';
  if (PATTERNS.CA.test(normalized)) return 'CA';
  if (PATTERNS.IE.test(normalized)) return 'IE';
  if (PATTERNS.NL.test(normalized)) return 'NL';
  if (PATTERNS.MT.test(normalized)) return 'MT';

  // Check 5-digit US before 4-digit AU to avoid collision
  if (PATTERNS.US.test(normalized)) return 'US';
  if (PATTERNS.AU.test(normalized)) return 'AU';

  // Special case: UK outward code only (legacy format)
  if (PATTERNS.GB_OUTWARD.test(normalized)) return 'GB';

  return null;
}

/**
 * Validates a postcode for its detected or specified country
 */
export function validatePostcode(postcode: string, countryCode?: CountryCode): boolean {
  const normalized = postcode.trim().toUpperCase();
  const country = countryCode || detectCountry(normalized);

  if (!country) return false;

  switch (country) {
    case 'GB':
      return PATTERNS.GB_FULL.test(normalized) || PATTERNS.GB_OUTWARD.test(normalized);
    case 'US':
      return PATTERNS.US.test(normalized);
    case 'CA':
      return PATTERNS.CA.test(normalized);
    case 'IE':
      return PATTERNS.IE.test(normalized);
    case 'NL':
      return PATTERNS.NL.test(normalized);
    case 'MT':
      return PATTERNS.MT.test(normalized);
    case 'AU':
      return PATTERNS.AU.test(normalized);
    default:
      return false;
  }
}

/**
 * Coarsens a postcode to area-level precision for privacy
 * Returns the coarsened postcode or the original if no rule exists
 */
export function coarsenPostcode(postcode: string, countryCode?: CountryCode): string {
  const normalized = postcode.trim().toUpperCase();
  const country = countryCode || detectCountry(normalized);

  if (!country) return normalized;

  const coarseningRule = COARSENING_RULES[country];
  return coarseningRule ? coarseningRule(normalized) : normalized;
}

/**
 * Normalizes a postcode (trim + uppercase)
 */
export function normalizePostcode(postcode: string): string {
  return postcode.trim().toUpperCase();
}

/**
 * Formats a postcode for display based on country conventions
 */
export function formatPostcodeDisplay(postcode: string, countryCode?: CountryCode): string {
  const normalized = normalizePostcode(postcode);
  const country = countryCode || detectCountry(normalized);

  if (!country) return normalized;

  switch (country) {
    case 'GB':
      // UK: Add space if it's a full postcode without one
      if (normalized.length > 4 && !normalized.includes(' ')) {
        const outward = normalized.slice(0, -3);
        const inward = normalized.slice(-3);
        return `${outward} ${inward}`;
      }
      return normalized;

    case 'CA':
      // Canada: Add space if missing (A1A 1A1 format)
      if (normalized.length === 6 && !normalized.includes(' ')) {
        return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
      }
      return normalized;

    case 'IE':
      // Ireland: Add space if missing (A12 BCDE format)
      if (normalized.length === 7 && !normalized.includes(' ')) {
        return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
      }
      return normalized;

    case 'NL':
      // Netherlands: Add space if missing (1234 AB format)
      if (normalized.length === 6 && !normalized.includes(' ')) {
        return `${normalized.slice(0, 4)} ${normalized.slice(4)}`;
      }
      return normalized;

    case 'MT':
      // Malta: Add space if missing (ABC 1234 format)
      if (normalized.length === 7 && !normalized.includes(' ')) {
        return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
      }
      return normalized;

    default:
      // US, AU: No spacing convention
      return normalized;
  }
}

/**
 * Gets country-specific fallback search patterns
 * For when exact postcode match returns no results
 */
export function getPostcodeFallbacks(postcode: string, countryCode?: CountryCode): string[] {
  const normalized = normalizePostcode(postcode);
  const country = countryCode || detectCountry(normalized);

  if (!country) return [];

  switch (country) {
    case 'GB': {
      // UK: First try exact outward code, then first 2 letters (area)
      const coarsened = coarsenPostcode(normalized, 'GB');
      if (coarsened.length >= 2) {
        return [coarsened.slice(0, 2)]; // e.g., "HP22" -> ["HP"]
      }
      return [];
    }

    case 'US': {
      // US: Try first 3 digits (broader area)
      if (normalized.length === 5) {
        return [normalized.slice(0, 3)]; // e.g., "90210" -> ["902"]
      }
      return [];
    }

    case 'CA': {
      // Canada: Try first letter (province/territory)
      const canadianCoarsened = coarsenPostcode(normalized, 'CA');
      if (canadianCoarsened.length >= 1) {
        return [canadianCoarsened.slice(0, 1)]; // e.g., "M4C" -> ["M"]
      }
      return [];
    }

    default:
      // Other countries: no fallback strategy yet
      return [];
  }
}
